import { type CvssScore } from '../cvss-score';
import { type CvssVectorParser } from '../cvss-vector-parser';
import { getCvssVersion, stripCvssVersionPrefix } from '../utils/cvss-utils';
import { CvssVersion } from '../cvss-enums';
import { Cvss2ScoringEngine } from './cvss2-scoring-engine';
import {
    AccessComplexity,
    AccessVector,
    Authentication,
    CollateralDamagePotential,
    Exploitability,
    Impact,
    ImpactSubscore,
    RemediationLevel,
    ReportConfidence,
    TargetDistribution,
} from './cvss2-enums';

/**
 * Implements a service offering CVSS v2 vector parsing.
 *
 * @remarks
 * Consumers should be aware that {@link parse} will raise an exception if an invalid CVSS v2 vector string is passed.
 * This includes strings containing incorrect keys/values and those that are missing required entries.
 *
 * @public
 */
export class Cvss2VectorParser implements CvssVectorParser {
    /**
     * Converts an access vector, represented as a string, into an enum value.
     *
     * @param accessVectorString - the string to convert
     * @returns the converted enum value
     */
    private static parseAccessVector(accessVectorString: string): AccessVector {
        switch (accessVectorString) {
            case 'L':
                return AccessVector.Local;
            case 'A':
                return AccessVector.AdjacentNetwork;
            case 'N':
                return AccessVector.Network;
        }
        throw new RangeError(
            `Invalid CVSS v2 access vector value: "${accessVectorString}"`,
        );
    }

    /**
     * Converts an access complexity, represented as a string, into an enum value.
     *
     * @param accessComplexityString - the string to convert
     * @returns the converted enum value
     */
    private static parseAccessComplexity(
        accessComplexityString: string,
    ): AccessComplexity {
        switch (accessComplexityString) {
            case 'H':
                return AccessComplexity.High;
            case 'M':
                return AccessComplexity.Medium;
            case 'L':
                return AccessComplexity.Low;
        }
        throw new RangeError(
            `Invalid CVSS v2 access complexity value: "${accessComplexityString}"`,
        );
    }

    /**
     * Converts an authentication level, represented as a string, into an enum value.
     *
     * @param authenticationString - the string to convert
     * @returns the converted enum value
     */
    private static parseAuthentication(
        authenticationString: string,
    ): Authentication {
        switch (authenticationString) {
            case 'M':
                return Authentication.Multiple;
            case 'S':
                return Authentication.Single;
            case 'N':
                return Authentication.None;
        }
        throw new RangeError(
            `Invalid CVSS v2 authentication value: "${authenticationString}"`,
        );
    }

    /**
     * Converts an impact magnitude, represented as a string, into an enum value.
     *
     * @param impactString - the string to convert
     * @returns the converted enum value
     */
    private static parseImpact(impactString: string): Impact {
        switch (impactString) {
            case 'N':
                return Impact.None;
            case 'P':
                return Impact.Partial;
            case 'C':
                return Impact.Complete;
        }
        throw new RangeError(`Invalid CVSS v2 impact value: "${impactString}"`);
    }

    /**
     * Converts an impact magnitude, represented as a string, into an enum value.
     *
     * @param impactString - the string to convert
     * @returns the converted enum value
     */
    private static parseExploitability(
        exploitabilityString: string,
    ): Exploitability {
        switch (exploitabilityString) {
            case 'ND':
                return Exploitability.NotDefined;
            case 'U':
                return Exploitability.UnprovenThatExplotExists;
            case 'POC':
                return Exploitability.ProofOfConceptCode;
            case 'F':
                return Exploitability.FunctionalExploitExists;
            case 'H':
                return Exploitability.High;
        }
        throw new RangeError(
            `Invalid CVSS v2 exploitability value: "${exploitabilityString}"`,
        );
    }

    /**
     * Converts a remediation level, represented as a string, into an enum value.
     *
     * @param remediationLevelString - the string to convert
     * @returns the converted enum value
     */
    private static parseRemediationLevel(
        remediationLevelString: string,
    ): RemediationLevel {
        switch (remediationLevelString) {
            case 'ND':
                return RemediationLevel.NotDefined;
            case 'OF':
                return RemediationLevel.OfficalFix;
            case 'TF':
                return RemediationLevel.TemporaryFix;
            case 'W':
                return RemediationLevel.Workaround;
            case 'U':
                return RemediationLevel.Unavailable;
        }
        throw new RangeError(
            `Invalid CVSS v2 remediation level value: "${remediationLevelString}"`,
        );
    }

    /**
     * Converts a report confidence level, represented as a string, into an enum value.
     *
     * @param reportConfidenceString - the string to convert
     * @returns the converted enum value
     */
    private static parseReportConfidence(
        reportConfidenceString: string,
    ): ReportConfidence {
        switch (reportConfidenceString) {
            case 'ND':
                return ReportConfidence.NotDefined;
            case 'UC':
                return ReportConfidence.Unconfirmed;
            case 'UR':
                return ReportConfidence.Uncorroborated;
            case 'C':
                return ReportConfidence.Confirmed;
        }
        throw new RangeError(
            `Invalid CVSS v2 report confidence value: "${reportConfidenceString}"`,
        );
    }

    /**
     * Converts a collateral damage potential, represented as a string, into an enum value.
     *
     * @param collateralDamagePotentialString - the string to convert
     * @returns the converted enum value
     */
    private static parseCollateralDamagePotential(
        collateralDamagePotentialString: string,
    ): CollateralDamagePotential {
        switch (collateralDamagePotentialString) {
            case 'ND':
                return CollateralDamagePotential.NotDefined;
            case 'N':
                return CollateralDamagePotential.None;
            case 'L':
                return CollateralDamagePotential.Low;
            case 'LM':
                return CollateralDamagePotential.LowMedium;
            case 'MH':
                return CollateralDamagePotential.MediumHigh;
            case 'H':
                return CollateralDamagePotential.High;
        }
        throw new RangeError(
            `Invalid CVSS v2 collateral damage potential value: "${collateralDamagePotentialString}"`,
        );
    }

    /**
     * Converts a target distribution, represented as a string, into an enum value.
     *
     * @param targetDistributionString - the string to convert
     * @returns the converted enum value
     */
    private static parseTargetDistribution(
        targetDistributionString: string,
    ): TargetDistribution {
        switch (targetDistributionString) {
            case 'ND':
                return TargetDistribution.NotDefined;
            case 'N':
                return TargetDistribution.None;
            case 'L':
                return TargetDistribution.Low;
            case 'M':
                return TargetDistribution.Medium;
            case 'H':
                return TargetDistribution.High;
        }
        throw new RangeError(
            `Invalid CVSS v2 target distribution value: "${targetDistributionString}"`,
        );
    }

    /**
     * Converts an impact subscore value, represented as a string, into an enum value.
     *
     * @param impactSubscoreString - the string to convert
     * @returns the converted enum value
     */
    private static parseImpactSubscore(
        impactSubscoreString: string,
    ): ImpactSubscore {
        switch (impactSubscoreString) {
            case 'ND':
                return ImpactSubscore.NotDefined;
            case 'L':
                return ImpactSubscore.Low;
            case 'M':
                return ImpactSubscore.Medium;
            case 'H':
                return ImpactSubscore.High;
        }
        throw new RangeError(
            `Invalid CVSS v2 impact subscore value: "${impactSubscoreString}"`,
        );
    }

    /**
     * Generates and returns a version-specific (CVSS v2) scoring engine loaded with a vector.
     *
     * @param vector - the vector to load in to the scoring engine
     * @returns the loaded scoring engine
     */
    public generateScoringEngine(vector: string): Cvss2ScoringEngine {
        // Variable to hold vector with version prefix stripped. Strip brackets if present.
        const strippedVector = stripCvssVersionPrefix(vector);

        // Ensure that the vector is a valid CVSS v2 vector.
        const version = getCvssVersion(vector);
        if (version !== CvssVersion.CVSS20) {
            throw new RangeError(
                `Invalid CVSS version: "${version}". Expected CVSS v2.`,
            );
        }

        // Get scoring engine ready.
        const cvss = new Cvss2ScoringEngine();

        // Split along slashes.
        const segments = strippedVector.split('/');
        for (const segment of segments) {
            // Split segment.
            const sections = segment.split(':');

            // Ensure that the segment is valid.
            const metric = sections[0];
            const value = sections[1];

            if (!metric || !value) {
                throw new RangeError(
                    `Invalid CVSS v2 vector segment: "${segment}"`,
                );
            }

            // Parse segment.
            switch (metric.toUpperCase()) {
                case 'AV':
                    cvss.accessVector =
                        Cvss2VectorParser.parseAccessVector(value);
                    break;
                case 'AC':
                    cvss.accessComplexity =
                        Cvss2VectorParser.parseAccessComplexity(value);
                    break;
                case 'AU':
                    cvss.authentication =
                        Cvss2VectorParser.parseAuthentication(value);
                    break;
                case 'C':
                    cvss.confidentialityImpact =
                        Cvss2VectorParser.parseImpact(value);
                    break;
                case 'I':
                    cvss.integrityImpact = Cvss2VectorParser.parseImpact(value);
                    break;
                case 'A':
                    cvss.availabilityImpact =
                        Cvss2VectorParser.parseImpact(value);
                    break;
                case 'E':
                    cvss.exploitability =
                        Cvss2VectorParser.parseExploitability(value);
                    break;
                case 'RL':
                    cvss.remediationLevel =
                        Cvss2VectorParser.parseRemediationLevel(value);
                    break;
                case 'RC':
                    cvss.reportConfidence =
                        Cvss2VectorParser.parseReportConfidence(value);
                    break;
                case 'CDP':
                    cvss.collateralDamagePotential =
                        Cvss2VectorParser.parseCollateralDamagePotential(value);
                    break;
                case 'TD':
                    cvss.targetDistribution =
                        Cvss2VectorParser.parseTargetDistribution(value);
                    break;
                case 'CR':
                    cvss.confidentialityRequirement =
                        Cvss2VectorParser.parseImpactSubscore(value);
                    break;
                case 'IR':
                    cvss.integrityRequirement =
                        Cvss2VectorParser.parseImpactSubscore(value);
                    break;
                case 'AR':
                    cvss.availabilityRequirement =
                        Cvss2VectorParser.parseImpactSubscore(value);
                    break;
                default:
                    throw new RangeError(
                        `Invalid CVSS v2 vector key: "${value}"`,
                    );
            }
        }

        // Return scoring engine.
        return cvss;
    }

    /**
     * {@inheritdoc}
     */
    public parse(vector: string): CvssScore {
        // Return computed score.
        return this.generateScoringEngine(vector).computeScore();
    }

    // TODO[CVSS]: Make sure that vector has proper order
    public reorderVector(vector: string): string {
        const version = getCvssVersion(vector);
        if (version !== CvssVersion.CVSS20) {
            throw new RangeError(
                `Invalid CVSS version: "${version}". Expected CVSS v2.`,
            );
        }
        return vector;
    }
}
